import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  FiUserPlus,
  FiRefreshCw,
  FiSearch,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiClipboard,
} from 'react-icons/fi';
import { registrationService } from '../../services/apiService';
import { useAuthStore } from '../../store/authStore';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDate } from '../../utils/formatters';

const RegistrationHistory = () => {
  const navigate = useNavigate();
  const lraRole = useAuthStore((state) => state.lraRole);
  const isAdmin = lraRole === 'admin';

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading, isRefetching, refetch } = useQuery(
    ['reg-history', page, limit],
    () => registrationService.listHistory({ limit, offset: page * limit }),
    { keepPreviousData: true }
  );

  if (isLoading) return <LoadingSpinner message="Loading history..." />;

  const rows = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Client-side filter (search + status)
  const filtered = rows.filter((r) => {
    if (status && r.status !== status) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.registration_id?.toLowerCase().includes(s) ||
      r.dssn?.toLowerCase().includes(s) ||
      r.tin?.toLowerCase().includes(s) ||
      r.business_name?.toLowerCase().includes(s)
    );
  });

  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };
  const handleNext = () => {
    if (page + 1 < totalPages) setPage(page + 1);
  };

  const successCount = rows.filter((r) => r.status === 'success').length;
  const failedCount = rows.filter((r) => r.status === 'failed').length;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {isAdmin ? 'All Registrations' : 'My Registrations'}
          </h1>
          <p className="page-subtitle">
            {total} total registration {total === 1 ? 'record' : 'records'}
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/lra/registration/new')}
          >
            <FiUserPlus size={18} />
            New Registration
          </button>
        </div>
      </div>

      {/* Quick stats */}
      {rows.length > 0 && (
        <div className="stats-grid" style={{ marginBottom: '20px' }}>
          <SmallStat label="Total (page)" value={rows.length} color="#1a73e8" />
          <SmallStat label="Success" value={successCount} color="#16a34a" />
          <SmallStat label="Failed" value={failedCount} color="#dc2626" />
          <SmallStat
            label="All Records"
            value={total}
            color="#7c3aed"
          />
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="form-row">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div className="search-bar" style={{ marginBottom: 0 }}>
              <FiSearch size={20} />
              <input
                type="text"
                placeholder="Search by registration ID, DSSN, TIN, business name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="duplicate">Duplicate</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {rows.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FiClipboard size={48} />
            <h3>No registrations yet</h3>
            <p>
              {isAdmin
                ? 'No taxpayer has been registered yet.'
                : 'You haven\'t registered any taxpayer yet.'}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/lra/registration/new')}
            >
              <FiUserPlus size={18} />
              Register First Taxpayer
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Registration</th>
                  <th>Business</th>
                  <th>DSSN</th>
                  <th>TIN</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: 24 }}>
                      No registrations match your search
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.registration_id}>
                      <td>
                        <div>
                          <code style={{ fontSize: '11px' }}>
                            {r.registration_id}
                          </code>
                          {!isAdmin && r.registered_by_officer_id && (
                            <div
                              style={{
                                fontSize: '10px',
                                color: '#9ca3af',
                                marginTop: 2,
                              }}
                            >
                              by {r.registered_by_officer_id}
                            </div>
                          )}
                          {isAdmin && r.registered_by_dssn && (
                            <div
                              style={{
                                fontSize: '10px',
                                color: '#9ca3af',
                                marginTop: 2,
                              }}
                            >
                              by {r.registered_by_dssn}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <strong>{r.business_name || '—'}</strong>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#9ca3af',
                            fontFamily: 'monospace',
                          }}
                        >
                          {r.business_id || ''}
                        </div>
                      </td>
                      <td>
                        <code style={{ fontSize: '12px' }}>{r.dssn}</code>
                      </td>
                      <td>
                        <code style={{ fontSize: '12px' }}>{r.tin}</code>
                      </td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                      <td>{formatDate(r.created_at)}</td>
                      <td>
                        <button
                          className="icon-btn"
                          onClick={() =>
                            navigate(`/lra/registration/${r.registration_id}`)
                          }
                          title="View details"
                        >
                          <FiEye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
                padding: '12px 16px',
                background: '#f9fafb',
                borderRadius: '8px',
              }}
            >
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                Page {page + 1} of {totalPages} • {total} total
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handlePrev}
                  disabled={page === 0}
                >
                  <FiChevronLeft size={16} />
                  Previous
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleNext}
                  disabled={page + 1 >= totalPages}
                >
                  Next
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Helpers ----------
const SmallStat = ({ label, value, color }) => (
  <div
    className="card"
    style={{
      padding: '16px',
      borderLeft: `4px solid ${color}`,
    }}
  >
    <div
      style={{
        fontSize: '26px',
        fontWeight: 700,
        color,
        lineHeight: 1.2,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: 4 }}>
      {label}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    success: { bg: '#dcfce7', color: '#166534' },
    failed: { bg: '#fee2e2', color: '#991b1b' },
    duplicate: { bg: '#fef3c7', color: '#92400e' },
    pending: { bg: '#dbeafe', color: '#1e40af' },
  };
  const c = colors[status] || colors.pending;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
};

export default RegistrationHistory;
